/*
 * File: ShareWidget.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 6, 2022 at 12:55 AM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 12:22 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Utilities
import { toastSuccess } from '../../utilities/toaster';

// Services
import { recordAction } from '../../services/action';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SHARE_OPTIONS, USER_ACTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './ShareWidget.styles';


/**
 * Component
 */

export const ShareWidget = ({
	context,
	items,
	className,
	closeWidget,
	fromBottom,
	content: {
		title: shareTitle, image: shareImage, url: shareURL
	},
	source: {
		type: sourceType,
		id: sourceId
	}
}) => {

	// Create references for elements
	const scrollRef = useRef();

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle render share action
	const renderShareAction = (option) => {

		// Initialize options
		let action = () => {};
		let title = '';
		let icon = [];
		let iconSize = '0.9rem';
		let color = '';

		// Get options for action
		switch (option) {
			case SHARE_OPTIONS.WHATSAPP:
				title = t('shareWidget.whatsAppTitle');
				icon = ['fab', 'whatsapp'];
				iconSize = '1.0rem';
				color = 'whatsAppBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Create content
					const content = `${shareTitle}: ${shareURL}`;

					// Share to WhatsApp
					const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(content)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.FACEBOOK:
				title = t('shareWidget.facebookTitle');
				icon = ['fab', 'facebook-f'];
				iconSize = '0.9rem';
				color = 'facebookBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Share to Facebook
					const url = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(shareURL)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.LINK:
				title = t('shareWidget.copyLinkTitle');
				icon = ['fas', 'link'];
				iconSize = '0.7rem';
				color = 'brandPrimaryBase';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Copy link to clipboard
					navigator.clipboard.writeText(shareURL);

					// Show success state
					toastSuccess(uiMode, partner, t('shareWidget.clipboardConfirmation'));

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.TWITTER:
				title = t('shareWidget.twitterTitle');
				icon = ['fab', 'twitter'];
				iconSize = '0.9rem';
				color = 'twitterBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Create content
					const content = `${shareTitle}`;

					// Share to Twitter
					const url = `https://x.com/share?url=${encodeURIComponent(shareURL)}&text=${encodeURIComponent(content)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.LINKEDIN:
				title = t('shareWidget.linkedinTitle');
				icon = ['fab', 'linkedin-in'];
				iconSize = '0.9rem';
				color = 'linkedinBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Share to LinkedIn
					const url = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(shareURL)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.REDDIT:
				title = t('shareWidget.redditTitle');
				icon = ['fab', 'reddit-alien'];
				iconSize = '0.9rem';
				color = 'redditBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Create content
					const content = `${shareTitle}`;

					// Share to Reddit
					const url = `https://reddit.com/submit?url=${encodeURIComponent(shareURL)}&title=${encodeURIComponent(content)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.PINTEREST:
				title = t('shareWidget.pinterestTitle');
				icon = ['fab', 'pinterest-p'];
				iconSize = '0.9rem';
				color = 'pinterestBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Share to Pinterest
					const url = `https://pinterest.com/pin/create/bookmarklet/?media=${encodeURIComponent(shareImage)}&url=${encodeURIComponent(shareURL)}&is_video=true&description=${encodeURIComponent(shareTitle)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			case SHARE_OPTIONS.EMAIL:
				title = t('shareWidget.emailTitle');
				icon = ['fas', 'envelope'];
				iconSize = '0.8rem';
				color = 'twitterBrand';
				action = () => {

					// Record action
					recordAction({
						action: USER_ACTIONS.SHARE_OPTION_CLICK,
						urlPath: window.location.pathname,
						context: {
							source_type: sourceType,
							source_id: sourceId,
							target: 'share_widget',
							action: option,
							context,
						}
					});

					// Create content
					const content = `${shareTitle}`;

					// Share to email
					const url = `mailto:?Subject=${encodeURIComponent(content)}&Body=${encodeURIComponent(shareURL)}`;
					window.open(url, '_blank');

					// Close widget
					closeWidget();
				};
				break;
			default:
				break;
		}

		// Render option
		return (
			<S.ShareAction onClick={action} key={option} className="animate">
				<S.IconContainer $color={color} $iconSize={iconSize}>
					<S.NavigationIcon icon={icon} />
				</S.IconContainer>
				<span>{title}</span>
			</S.ShareAction>
		);
	};

	// Handle actions on class change
	useEffect(() => {

		// Update scroll position
		if (className.includes('show')) {
			scrollRef.current.scrollTo(0, 0);
		}
	}, [className]);


	// Render component
	return (
		<S.ShareWidget className={className} $fromBottom={fromBottom}>
			<S.NavigationContainer ref={scrollRef}>
				{items.map((item) => renderShareAction(item))}
			</S.NavigationContainer>
		</S.ShareWidget>
	);
};


/**
 * Configuration
 */

ShareWidget.displayName = 'ShareWidget';
ShareWidget.propTypes = {
	context: PropTypes.string,
	items: PropTypes.arrayOf(PropTypes.string),
	fromBottom: PropTypes.bool,
	className: PropTypes.string,
	closeWidget: PropTypes.func,
	content: PropTypes.shape({
		title: PropTypes.string,
		description: PropTypes.string,
		image: PropTypes.string,
		url: PropTypes.string
	}),
	source: PropTypes.shape({
		type: PropTypes.string,
		id: PropTypes.string
	})
};
ShareWidget.defaultProps = {
	context: null,
	items: [],
	fromBottom: false,
	className: null,
	closeWidget: null,
	content: {},
	source: {}
};
